import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, Circle, AlertCircle, Eye } from "lucide-react";

export const ItStakeholderRaciMatrix = () => {
  const raciData = [
    {
      activity: "Technology Strategy",
      cto: "A",
      cio: "C",
      itDirector: "I",
      devops: "I",
      productMgr: "C",
    },
    {
      activity: "IT Budget Approval",
      cto: "C",
      cio: "A",
      itDirector: "R",
      devops: "I",
      productMgr: "I",
    },
    {
      activity: "Infrastructure Decisions",
      cto: "C",
      cio: "A",
      itDirector: "R",
      devops: "C",
      productMgr: "I",
    },
    {
      activity: "Security & Compliance",
      cto: "I",
      cio: "A",
      itDirector: "R",
      devops: "C",
      productMgr: "I",
    },
    {
      activity: "Product Roadmap",
      cto: "A",
      cio: "I",
      itDirector: "I",
      devops: "I",
      productMgr: "R",
    },
    {
      activity: "Deployment & CI/CD",
      cto: "I",
      cio: "I",
      itDirector: "A",
      devops: "R",
      productMgr: "I",
    },
    {
      activity: "Vendor Selection",
      cto: "C",
      cio: "A",
      itDirector: "R",
      devops: "C",
      productMgr: "C",
    },
    {
      activity: "Incident Response",
      cto: "I",
      cio: "A",
      itDirector: "R",
      devops: "R",
      productMgr: "I",
    },
  ];

  const getRaciStyle = (value: string) => {
    switch (value) {
      case "R":
        return {
          bg: "bg-primary/15",
          text: "text-primary",
          icon: CheckCircle,
          label: "Responsible",
        };
      case "A":
        return {
          bg: "bg-chart-2/15",
          text: "text-chart-2",
          icon: AlertCircle,
          label: "Accountable",
        };
      case "C":
        return {
          bg: "bg-chart-4/15",
          text: "text-chart-4",
          icon: Circle,
          label: "Consulted",
        };
      case "I":
        return {
          bg: "bg-muted",
          text: "text-muted-foreground",
          icon: Eye,
          label: "Informed",
        };
      default:
        return { bg: "", text: "", icon: Circle, label: "" };
    }
  };

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-4">
        RACI Matrix: Who Does What
      </h2>
      <p className="text-muted-foreground mb-6 max-w-3xl">
        The RACI matrix clarifies responsibilities for common IT activities. 
        <strong> R</strong>=Responsible (does the work), <strong>A</strong>=Accountable (final decision), 
        <strong> C</strong>=Consulted (provides input), <strong>I</strong>=Informed (kept updated).
      </p>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6">
        {["R", "A", "C", "I"].map((letter) => {
          const style = getRaciStyle(letter);
          return (
            <div key={letter} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${style.bg}`}>
              <span className={`font-bold ${style.text}`}>{letter}</span>
              <span className={`text-sm ${style.text}`}>{style.label}</span>
            </div>
          );
        })}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold text-foreground">Activity</TableHead>
              <TableHead className="text-center font-semibold text-foreground">CTO</TableHead>
              <TableHead className="text-center font-semibold text-foreground">CIO</TableHead>
              <TableHead className="text-center font-semibold text-foreground">IT Director</TableHead>
              <TableHead className="text-center font-semibold text-foreground">DevOps</TableHead>
              <TableHead className="text-center font-semibold text-foreground">Product Mgr</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {raciData.map((row, index) => (
              <TableRow key={index} className="hover:bg-muted/30">
                <TableCell className="font-medium text-foreground">{row.activity}</TableCell>
                {[row.cto, row.cio, row.itDirector, row.devops, row.productMgr].map((value, idx) => {
                  const style = getRaciStyle(value);
                  return (
                    <TableCell key={idx} className="text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${style.bg} ${style.text} font-bold text-sm`}>
                        {value}
                      </span>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
};
